import { action, autorun, computed, observable, makeObservable } from "mobx";
import { fromPromise, type IPromiseBasedObservable, PENDING } from "mobx-utils";

import Api from "nvent-web/services/Api";
import { type DetailedRoom } from "nvent-web/types/DetailedRoom";
import { type FloorFinishOption } from "nvent-web/types/FloorFInishOption";
import { type NewRoomFormValues } from "nvent-web/types/NewRoomFormValues";
import { type Option } from "nvent-web/types/Option";
import { type Room } from "nvent-web/types/Room";
import { createInitialFromPromise } from "nvent-web/utils/createInitialFromPromise";
import type { Navigate } from "nvent-web/utils/withRouter";

export class RoomLocalState {
  isExpanded = false;

  constructor(roomId: number) {
    makeObservable(this, {
      isExpanded: observable,
      toggleExpanded: action.bound,
    });

    const key = `rooms.${roomId}.state`;
    const stored = localStorage.getItem(key);

    if (stored) {
      Object.assign(this, JSON.parse(stored));
    }

    autorun(() => {
      localStorage.setItem(
        key,
        JSON.stringify({ isExpanded: this.isExpanded })
      );
    });
  }

  toggleExpanded(isExpanded = !this.isExpanded) {
    this.isExpanded = isExpanded;
  }
}

export class RoomsStore {
  newRoomPromise?: IPromiseBasedObservable<Room>;
  roomDetailsPromise?: IPromiseBasedObservable<DetailedRoom>;
  roomDetails?: DetailedRoom;
  removeRoomPromise?: IPromiseBasedObservable<void>;
  copyRoomPromise?: IPromiseBasedObservable<void>;
  floorFinishOptionsPromise = createInitialFromPromise<FloorFinishOption[]>([]);
  floorConstructionOptionsPromise = createInitialFromPromise<Option[]>([]);

  private localStateMap = observable.map<number, RoomLocalState>();

  constructor(
    private api: Api,
    private navigate: Navigate
  ) {
    makeObservable(this, {
      newRoomPromise: observable,
      roomDetailsPromise: observable,
      roomDetails: observable,
      removeRoomPromise: observable,
      copyRoomPromise: observable,
      floorFinishOptionsPromise: observable,
      floorConstructionOptionsPromise: observable,
      getLocalState: action.bound,
      isRoomCreating: computed,
      createRoom: action.bound,
      areRoomDetailsLoading: computed,
      getRoom: action.bound,
      removeRoom: action.bound,
      copyRoom: action.bound,
      updateRoom: action.bound,
      areFloorFinishOptionsLoading: computed,
      areFloorContructionOptionsLoading: computed,
      loadFloorFinishOptions: action.bound,
      loadFloorConstructionOptions: action.bound,
      setRoomDetails: action.bound,
      floorFinishOptions: computed,
      floorConstructionOptions: computed,
    });

    this.loadFloorFinishOptions();
    this.loadFloorConstructionOptions();

    autorun(() => {
      if (this.roomDetailsPromise) {
        this.setRoomDetails(
          this.roomDetailsPromise.case({ fulfilled: (r) => r }) ||
            this.roomDetails
        );
      }
    });
  }

  setRoomDetails(value: DetailedRoom | undefined) {
    this.roomDetails = value;
  }

  getLocalState(id: number): RoomLocalState {
    let state = this.localStateMap.get(id);

    if (!state) {
      state = new RoomLocalState(id);
      this.localStateMap.set(id, state);
    }

    return state;
  }

  get isRoomCreating(): boolean {
    return Boolean(
      this.newRoomPromise && this.newRoomPromise.state === PENDING
    );
  }

  async createRoom({
    values,
    projectId,
    levelId,
  }: {
    values: NewRoomFormValues;
    projectId: number;
    levelId?: number;
  }) {
    this.newRoomPromise = fromPromise(
      this.api.rooms
        .create({ data: values, projectId, levelId })
        .then(({ data }) => data)
    );
    await this.newRoomPromise;

    this.navigate(-1);
  }

  get areRoomDetailsLoading() {
    return Boolean(
      this.roomDetailsPromise && this.roomDetailsPromise.state === PENDING
    );
  }

  async getRoom(projectId: number, roomId: number) {
    this.roomDetailsPromise = fromPromise(
      this.api.rooms.getOne(projectId, roomId).then(({ data }) => data)
    );
    return this.roomDetailsPromise;
  }

  async removeRoom(projectId: number, roomId: number) {
    this.removeRoomPromise = fromPromise(
      this.api.rooms.remove(projectId, roomId).then(({ data }) => data)
    );
    return this.removeRoomPromise;
  }

  async copyRoom(projectId: number, roomId: number) {
    this.copyRoomPromise = fromPromise(
      this.api.rooms.copy(projectId, roomId).then(({ data }) => data)
    );
    return this.copyRoomPromise;
  }

  async updateRoom(
    values: Partial<NewRoomFormValues>,
    projectId: number,
    id: number
  ) {
    this.newRoomPromise = fromPromise(
      this.api.rooms.updateOne(values, projectId, id).then(({ data }) => data)
    );

    await this.newRoomPromise;
  }

  get areFloorFinishOptionsLoading(): boolean {
    return this.floorFinishOptionsPromise.state === PENDING;
  }

  get areFloorContructionOptionsLoading(): boolean {
    return this.floorConstructionOptionsPromise.state === PENDING;
  }

  loadFloorFinishOptions() {
    this.floorFinishOptionsPromise = fromPromise(
      this.api.options.getFloorFinish().then(({ data }) => data)
    );
  }

  loadFloorConstructionOptions() {
    this.floorConstructionOptionsPromise = fromPromise(
      this.api.options.getFloorConstruction().then(({ data }) => data)
    );
  }

  get floorFinishOptions(): FloorFinishOption[] {
    return (
      this.floorFinishOptionsPromise.case({
        fulfilled: (floors) => floors,
      }) || []
    );
  }

  get floorConstructionOptions(): Option[] {
    return (
      this.floorConstructionOptionsPromise.case({
        fulfilled: (floors) => floors,
      }) || []
    );
  }
}
